'use client';

import { ProductDocument } from 'models/product';
import sanitize from 'sanitize-html';
import { useState, useRef, useEffect } from 'react';

const ProductDescription = ({
  product,
  hideReadMore = false,
  hideTitle = false,
}: {
  product: ProductDocument;
  hideReadMore?: boolean;
  hideTitle?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const contentRef = useRef<HTMLDivElement | HTMLParagraphElement>(null);

  useEffect(() => {
    // Use a timeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (contentRef.current && !isExpanded) {
        // Check if content overflows by comparing scrollHeight with clientHeight
        // When line-clamp-5 is applied, scrollHeight will be greater than clientHeight if content exceeds 5 lines
        const hasOverflow =
          contentRef.current.scrollHeight > contentRef.current.clientHeight;
        setShowExpandButton(hasOverflow);
      } else if (isExpanded) {
        // When expanded, we know it was long enough to show the button
        setShowExpandButton(true);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [product.description_html, product.description, isExpanded]);

  const descriptionContent = product.description_html ? (
    <div
      ref={contentRef as React.RefObject<HTMLDivElement>}
      className={`editor-content overflow-x-auto text-[1.6rem] [&>ul]:list-inside [&>ul]:list-disc ${
        !isExpanded ? 'line-clamp-5' : ''
      }`}
      dangerouslySetInnerHTML={{
        __html: sanitize(product.description_html, {
          allowedTags: sanitize.defaults.allowedTags.filter(
            (tag) => tag !== 'a'
          ),
        }),
      }}
    />
  ) : product.description ? (
    <p
      ref={contentRef as React.RefObject<HTMLParagraphElement>}
      className={`overflow-x-auto whitespace-pre-line text-[1.6rem] ${
        !isExpanded ? 'line-clamp-5' : ''
      }`}
    >
      {product.description}
    </p>
  ) : (
    <span className="italic"> No description available</span>
  );

  return (
    <>
      {!hideTitle && (
        <h2 className="mb-[1rem] mt-0 text-[1.8rem] font-semibold sm:mt-[2rem]">
          Description
        </h2>
      )}

      <div>
        {descriptionContent}
        {showExpandButton && !hideReadMore && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-4 text-[1.6rem] font-medium text-black underline hover:opacity-80"
          >
            {isExpanded ? 'See less' : 'See full description'}
          </button>
        )}
      </div>
    </>
  );
};

export default ProductDescription;
